import { Button, Table, Modal } from "@telluscompany/design-system";
import { useSearchParams } from "react-router-dom";
import { PlusCircle } from "lucide-react";
import { DeleteModal } from "@telluscompany/design-system";
import { useLocalization } from "@i18n";
import { useCrudModals } from "@shared/hooks";
import { Gs3TablePaginator } from "@shared/components";
import { EMPTY_PAGINATION_META } from "@shared/constants/pagination.constants";
import { ClientFormData } from "../../schemas";
import { CreateClientForm } from "./CreateClientForm";
import { EditClientForm } from "./EditClientForm";
import { ClientsFilter } from "./ClientsFilter";
import { ClientsActionMenu } from "./ClientsActionMenu";
import {
  ListClientsResponse,
  useCreateClientMutation,
  useDeleteClientMutation,
  useEditClientMutation,
  useUploadLogoMutation,
} from "../../api/client";
import { PaginationMeta } from "@shared/api";
import { useUpdateTableUserPreferences } from "@shared/hooks";
import { ClientFilterData } from "../../types";
import { useUserContext } from "@/shared/contexts";

interface ClientsTabProps {
  clients: ListClientsResponse[];
  onFilter: (filters: ClientFilterData) => void;
  onClearFilter: () => void;
  initialFilters?: Partial<ClientFilterData>;
  paginatedMeta?: PaginationMeta;
}

export const ClientsTab = ({
  clients,
  onFilter,
  onClearFilter,
  initialFilters,
  paginatedMeta,
}: ClientsTabProps) => {
  const { translate } = useLocalization();
  const {
    modals,
    selectedItem: selectedClient,
    openCreate,
    openEdit,
    openDelete,
    closeCreate,
    closeEdit,
    closeDelete,
  } = useCrudModals<ListClientsResponse>();

  const { mutate: createClient } = useCreateClientMutation();
  const { mutate: editClient } = useEditClientMutation();
  const { mutate: deleteClient, isPending: isDeleting } =
    useDeleteClientMutation();
  const { mutate: uploadLogo } = useUploadLogoMutation();

  const { user } = useUserContext();
  const updateTableUserPreferences = useUpdateTableUserPreferences(user?.userPreferences);

  const handleCreateClient = (data: ClientFormData) => {
    const { logo, responsible, ...rest } = data;
    const hasResponsible =
      responsible &&
      (responsible.name || responsible.email || responsible.phone);

    createClient(
      {
        ...rest,
        responsible: hasResponsible ? responsible : undefined,
      },
      {
        onSuccess: (response) => {
          if (response && logo?.[0]) {
            uploadLogo({ id: response, logo: logo[0] as File });
          }
          closeCreate();
        },
      },
    );
  };

  const handleEditClient = (data: ClientFormData) => {
    if (!selectedClient) return;

    const { logo, responsible, ...rest } = data;
    const hasResponsible =
      responsible &&
      (responsible.name || responsible.email || responsible.phone);

    editClient(
      {
        ...rest,
        id: selectedClient.id,
        responsible: hasResponsible ? responsible : undefined,
      },
      {
        onSuccess: (response) => {
          if (response?.id && logo?.[0]) {
            uploadLogo({ id: response.id, logo: logo[0] as File });
          }
          closeEdit();
        },
        onError: () => closeEdit(),
      },
    );
  };

  const handleDeleteClient = () => {
    const id = selectedClient?.id;
    if (!id) return;

    deleteClient(id, {
      onSuccess: () => closeDelete(),
      onError: () => closeDelete(),
    });
  };

  const createActionMenu = (rowData: { rowData: ListClientsResponse }) => {
    const client = rowData.rowData;
    return (
      <ClientsActionMenu
        client={client}
        onEdit={() => openEdit(client)}
        onDelete={() => openDelete(client)}
      />
    );
  };

  const tableColumns = [
    {
      key: "name",
      label: translate("management.clients.table.column.client"),
      sortType: "text" as const,
      minWidth: "150px",
    },
    {
      key: "fantasyName",
      label: translate("management.clients.table.column.tradeName"),
      sortType: "text" as const,
      minWidth: "150px",
    },
    {
      key: "cnpj",
      label: translate("management.clients.table.column.cnpj"),
      sortType: "text" as const,
      minWidth: "150px",
    },
    {
      key: "responsibleName",
      label: translate("management.clients.table.column.responsible"),
      sortType: "text" as const,
      minWidth: "150px",
    },
  ];

  return (
    <div className="p-4">
      <Table<ListClientsResponse>
        userId="CLIENT1"
        usersPreferencesFromServer={user?.userPreferences}
        updateUsersPreferencesOnServer={updateTableUserPreferences}
        useSearchParamsHook={useSearchParams}
        enableSortByUrl
        TableActionMenu={ user?.role === "owner" ? createActionMenu as React.ComponentType : undefined}
        TableButtons={
          user?.role === "owner" && (
            <Button
            size="small"
            variant="primary"
            onClick={openCreate}
            iconLeft={<PlusCircle />}
          >
            {translate("management.clients.button.new")}
          </Button>
          )
        }
        TableFilter={() => (
          <ClientsFilter
            onFilter={onFilter}
            onClear={onClearFilter}
            initialFilters={initialFilters}
          />
        )}
        TablePaginator={
          <Gs3TablePaginator
            meta={paginatedMeta ?? EMPTY_PAGINATION_META}
            useUrlState
            useSearchParamsHook={useSearchParams}
          />
        }
        draggable
        enableTableColumnsSelector
        tableColumns={tableColumns}
        tableData={clients.map(
          (client) =>
            ({ ...client }) as { [key: string]: unknown; color?: string },
        )}
        tableId="clients-table"
        filterButtonText={translate(
          "management.clients.table.filterButtonText",
        )}
      />

      {/* Modal de Criação */}
      <Modal
        open={modals.create}
        onClose={closeCreate}
        title={translate("management.clients.modal.title.create")}
        content={
          <CreateClientForm
            onSubmit={handleCreateClient}
            onCancel={closeCreate}
          />
        }
      />

      {/* Modal de Edição */}
      <Modal
        open={modals.edit}
        onClose={closeEdit}
        title={translate("management.clients.modal.title.edit")}
        content={
          <EditClientForm
            clientId={selectedClient?.id ?? undefined}
            onSubmit={handleEditClient}
            onCancel={closeEdit}
          />
        }
      />

      {/* Modal de Exclusão */}
      <DeleteModal
        open={modals.delete}
        onClose={closeDelete}
        onConfirm={handleDeleteClient}
        isDeleting={isDeleting}
        itemDescription={selectedClient?.name}
        title={translate("common.deleteModalTitle")}
        message={translate("common.deleteModalItemMessage")}
        alertMessage={translate("common.deleteModalAlertMessage")}
        confirmButtonText={translate("common.delete")}
        cancelButtonText={translate("common.cancel")}
        deletingButtonText={translate("common.deleting")}
      />
    </div>
  );
};

export default ClientsTab;
